import { UserPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useHasPermission } from "../auth/permissions-context";
import { RedoMerchantRpcClientContext } from "../rpc-client";

export function useViewsAndFolders<
  TView,
  TFolder,
  TReturnType extends ReturnTypeEnum,
>(
  type: TReturnType,
  ...permissions: UserPermissions[]
): { views: TView[]; folders: TFolder[]; reloadViews: () => Promise<void> } {
  const team = useRequiredContext(TeamContext);
  const client = useContext(RedoMerchantRpcClientContext);
  const canViewOrderManagement = useHasPermission(...permissions);

  const [views, setViews] = useState<TView[]>([]);
  const [folders, setFolders] = useState<TFolder[]>([]);

  const [_, loadViews] = useTriggerLoad(async (signal) => {
    if (!client || !canViewOrderManagement) {
      return;
    }

    const { views, folders } = await client.getReturnViewsAndFolders(
      { type },
      { signal },
    );
    setViews(views as TView[]);
    setFolders(folders as TFolder[]);
  });

  const reloadViews = useHandler(async () => {
    loadViews();
  });

  useEffect(() => {
    void reloadViews();
  }, [client, reloadViews, team, canViewOrderManagement]);

  return { views, folders, reloadViews };
}

export function useActiveView<TView extends { name: string }>(
  viewsContext: { teamViews?: TView[] },
  createViewName: string,
  defaultView: TView,
  staticViews: Record<string, TView>,
): {
  currentView: TView | undefined;
  editingView: boolean;
  setEditingView: Dispatch<SetStateAction<boolean>>;
} {
  const viewName = useParams().viewName;
  const [editingView, setEditingView] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<TView>();

  useEffect(() => {
    if (currentView?.name === createViewName) {
      setEditingView(true);
    } else {
      setEditingView(false);
    }
  }, [currentView, createViewName]);

  useEffect(() => {
    if (viewName === createViewName) {
      setCurrentView(staticViews[createViewName]);
      return;
    }

    // Fall back to the static definition so deep links to a system view work
    // even before the team's copy is seeded (mirrors order-tracking shipments).
    const view =
      viewsContext.teamViews?.find((view) => view.name === viewName) ??
      (viewName ? staticViews[viewName] : undefined);

    if (!view) {
      setCurrentView(defaultView);
    } else {
      setCurrentView(view);
    }
  }, [viewsContext, viewName, createViewName, defaultView, staticViews]);

  return { currentView, editingView, setEditingView };
}
